/** @jsx jsx */

import { Fragment } from 'react';
import { capitalize } from 'lodash';

import { jsx } from '@emotion/core';

import Link from 'app/common/components/Link';
import LinkWithArrow from 'app/developer-v3/components/LinkWithArrow';
import { GUIDE_SECTION_IDS } from 'app/developer-v3/components/SidebarRight/constants';
import { Colors, Text } from '@zapier/design-system';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { PARTNER_CONTACT_FORM_PATH } from 'app/developer-v3/constants';
import { GET_HELP_CTA } from 'app/developer-v3/teams/team-technical-support-operations/get-help';

const GUIDE_SECTIONS = [
  {
    id: GUIDE_SECTION_IDS.ZAPIER_PLATFORM,
    title: 'What is this?',
    titleCondensed: 'What is the Zapier Platform?',
    content: (
      <Fragment>
        <Text margin="0px 0px 15px" tag="p">
          It’s a tool for you to build a Zapier integration for your software
          product. Via the Zapier Platform, your product will integrate with the
          thousands of other products on Zapier.
        </Text>
        <Text margin="0px 0px 15px" tag="p">
          If you’ve used other API integration tools, the Zapier Platform works
          similarly, but with a few key differences. With the Zapier Platform,
          you're not just connecting your API to another API: you’re defining a
          user interface where users map data in and out of each of your
          endpoints. Zapier puts the user in control so they can create their
          own customized integration workflows between your product and others!
        </Text>
        <LinkWithArrow href="https://docs.zapier.com/platform/quickstart/how-zapier-works">
          Learn more
        </LinkWithArrow>
      </Fragment>
    ),
    contentCondensed:
      'It’s a tool for you to build a Zapier integration for your software product',
    icon: 'zapierLogo' as const,
    iconBackgroundColor: Colors.Yellow1,
  },
  {
    id: GUIDE_SECTION_IDS.BUILD,
    title: 'What do you need?',
    titleCondensed: 'What’s needed to build on the platform?',
    content: (
      <Fragment>
        <Text margin="0px 0px 15px" tag="p">
          &bull; An API for your product
        </Text>
        <Text margin="0px 0px 15px" tag="p">
          {/* TODO: Pass appId in here and link to the Manage Team page */}
          &bull; Knowledge of how APIs work. Not a developer? Invite your
          technical team to collaborate with you on your new Zapier project.
        </Text>
        <Text margin="0px 0px 15px" tag="p">
          &bull; Familiarity with how Zapier works. If you haven't done so
          already,{' '}
          <Link href={`${ZAPIER_APP_BASE_URL}/app/editor/`} target="_blank">
            make a Zap
          </Link>{' '}
          or two to get familiar with the components you'll need to build for
          your integration.
        </Text>
      </Fragment>
    ),
    contentCondensed:
      'Your product must have an API and you’ll need a basic knowledge of how APIs work',
    icon: 'fileGuideFill' as const,
    iconBackgroundColor: Colors.Red2,
  },
  {
    id: GUIDE_SECTION_IDS.TOUR,
    title: 'Take a tour of the tool',
    titleCondensed: 'How does the platform work?',
    content: (
      <Fragment>
        <Text margin="0px 0px 15px" tag="p">
          Get a step by step walk-through of a Zapier integration in our Quick
          Start Guide.
        </Text>

        <LinkWithArrow href="https://docs.zapier.com/platform/quickstart/how-zapier-works">
          Get started
        </LinkWithArrow>
      </Fragment>
    ),
    contentCondensed:
      'Get a step-by-step walk-through of how to build a Zapier integration.',
    icon: 'actionExploreFill' as const,
    iconBackgroundColor: Colors.Blue2,
  },
  {
    id: GUIDE_SECTION_IDS.GET_STARTED,
    title: 'Ready to get started?',
    titleCondensed: 'Ready to get started?',
    content: (
      <Fragment>
        <Text margin="0px 0px 15px" tag="p">
          The first step is configuring a way for your users to authenticate
          themselves. Your Zapier integration will define a front end where a
          user can enter a user name and password, a API key, get directed to
          your site in an OAuth flow, etc. Build and test this component first,
          then you'll be able to easily test calls to your API.{' '}
        </Text>
        <LinkWithArrow href="https://docs.zapier.com/platform/build/auth">
          Learn more
        </LinkWithArrow>
      </Fragment>
    ),
    contentCondensed:
      'The first step is to configure a way for your users to authenticate themselves.',
    icon: 'miscBolt' as const,
    iconBackgroundColor: Colors.StatusSuccessWeaker,
  },
  {
    id: GUIDE_SECTION_IDS.VERSIONS,
    title: 'Versioning in Zapier Integrations',
    titleCondensed: 'Learn about versions for Zapier Integrations',
    content: (
      <Fragment>
        <div>
          <Text fontWeight={600} margin="0px 0px 15px" tag="h3" type="Body4">
            Versioning in Zapier Integrations
          </Text>
          <Text margin="0px 0px 15px" tag="p">
            Zapier integrations use versions to help you manage changes to your
            project over time. For instance, your version "1.0.0" may be the one
            you've published. Meanwhile, you may be developing on a version
            "1.1.0" that only you and your team can see and use.
          </Text>
          <Text margin="0px 0px 15px" tag="p">
            Your integration has a title, and logo, a set of admins, and other
            attributes that are shared by all versions of the integration.
            However, all of the actual functionality for your integration lives
            in a version: auth config, triggers, actions, searches.
          </Text>
        </div>
        <div>
          <Text fontWeight={600} margin="0px 0px 15px" tag="h3" type="Body4">
            When you can no longer edit a version
          </Text>
          <Text margin="0px 0px 15px" tag="p">
            Versions that have a significant number of users (~5) may no longer
            be edited directly. Versions that have been made public are also
            immutable. You'll need to create a new version to make any change.
          </Text>
          <Text margin="0px 0px 15px" tag="p">
            Why? This is to prevent a poor user experience that might be created
            when an integration changes in the middle of a running workflow.
          </Text>
        </div>
        <div>
          <Text fontWeight={600} margin="0px 0px 15px" tag="h3" type="Body4">
            How do I create a new version?
          </Text>
          <Text margin="0px 0px 15px" tag="p">
            To create a new version in the UI, click "Versions" in the left-hand
            nav menu. Then click the cog icon next to an existing version and
            select "Clone". This creates a complete copy of a version and
            assigns the new version a number.
          </Text>
          <Text margin="0px 0px 15px" tag="p">
            If you're developing with the CLI, just change the version number in
            your package.json file and run zapier push to create a new version.
          </Text>
        </div>
        <div>
          <Text fontWeight={600} margin="0px 0px 15px" tag="h3" type="Body4">
            Migrating users between versions
          </Text>
          <Text margin="0px 0px 15px" tag="p">
            You can move your users from one version to another with the migrate
            tool. Users will always remain on your old version until you do this
            explicitly, even after you promote a new version.
          </Text>
        </div>
        <div>
          <Text fontWeight={600} margin="0px 0px 15px" tag="h3" type="Body4">
            Breaking changes
          </Text>
          <Text margin="0px 0px 15px" tag="p">
            Be mindful of breaking changes that will create errors when moving
            Zaps from an older version to the new version. These are things like
            changing the auth type, adding new required fields, removing
            triggers, etc. When making a breaking change, don't migrate users
            across the breaking change. Consider deprecating the older version
            instead if you need to force users to upgrade their workflows to the
            new version.
          </Text>
        </div>
        <div>
          <Text fontWeight={600} margin="0px 0px 15px" tag="h3" type="Body4">
            Semver
          </Text>
          <Text margin="0px 0px 15px" tag="p">
            We strongly encourage you to follow{' '}
            <Link href="https://semver.org/" target="_blank">
              semver
            </Link>{' '}
            conventions when numbering your versions so you can remember which
            versions introduced breaking changes.
          </Text>
        </div>
        <div>
          <Text fontWeight={600} margin="0px 0px 15px" tag="h3" type="Body4">
            Versions & the CLI
          </Text>
          <Text margin="0px 0px 15px" tag="p">
            Projects starting in the UI can be opened by the Zapier CLI and your
            work can continue with that tool by creating new versions with it.
            The code and configuration of versions created with the CLI will not
            be editable in the UI, but you'll be able to see them and manage
            them from the UI.
          </Text>
        </div>
      </Fragment>
    ),
    contentCondensed:
      'Zapier integrations use versions to help you manage changes to your project over time',
    icon: 'arrowBranch' as const,
    iconBackgroundColor: Colors.Yellow1,
  },
  {
    id: GUIDE_SECTION_IDS.HELP,
    title: 'Where to go for help',
    titleCondensed: 'Where can you go for help?',
    content: (
      <Fragment>
        <Text margin="0px 0px 15px" tag="p">
          &bull;{' '}
          <Link href="https://docs.zapier.com" target="_blank">
            Docs
          </Link>
        </Text>
        <Text margin="0px 0px 15px" tag="p">
          &bull;{' '}
          <Link
            href="https://community.zapier.com/p/developer-zone"
            target="_blank"
          >
            Developer community
          </Link>
        </Text>
        <Text margin="0px 0px 15px" tag="p">
          &bull;{' '}
          <Link href={PARTNER_CONTACT_FORM_PATH} target="_blank">
            {GET_HELP_CTA.split(' ').map(capitalize).join(' ')}
          </Link>
        </Text>
      </Fragment>
    ),
    contentCondensed: 'Read our documentation or reach out to our team.',
    icon: 'commentBlankFill' as const,
    iconBackgroundColor: Colors.Blue2,
  },
];

export { GUIDE_SECTIONS };
